import {
  InfoCircleOutlined,
  MinusCircleOutlined,
  PlusOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import {
  Button,
  Col,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Space,
  Switch,
  Tooltip,
  message,
} from 'antd';
import axios from 'axios';
import { useContext, useEffect, useState } from 'react';
import { AiOutlineInfoCircle } from 'react-icons/ai';
import { BsBox2, BsBoxSeam, BsImages, BsRulers, BsUpcScan } from 'react-icons/bs';
import { Link } from 'react-router-dom';

import useStore from '../../store/useStore';
import { commonRegExp, getProductSizeOptions, getProductWeightOptions } from '../../utils';
import { formatThousands, parseThousands } from '../../utils/numberInput';
import useBrandStore from '../profile/brandStore';
import FormFooter from './components/FormFooter';
import SectionCard from './components/SectionCard';
import ImageUpload from './ImageUpload';
import { LabelContext } from './ProductDataContext';
import SingleImageUpload from './SingleImageUpload';
import { findMissingSkuFields, getSkuRequiredFields, missingSkuFieldsMessage } from './sku';
import {
  PRODUCT_DESCRIPTION_MAX_LENGTH,
  PRODUCT_TITLE_MAX_LENGTH,
  nonNegativeRule,
  productDescriptionRules,
  productTitleRules,
  requiredText,
  trimTextFields,
  wholeNumberRule,
} from '../../utils/formRules';

const { TextArea } = Input;

/** Талбарууд картын бүтэн өргөнөөр сунахгүй — зүүн талд багцлагдана */
const FORM_MAX_WIDTH = 960;

/** Хадгалахын өмнө эхэн/төгсгөлийн илүү зайг цэвэрлэх текст талбарууд */
const TRIMMABLE_TEXT_FIELDS = ['title', 'detail', 'size', 'color', 'type'];

/**
 * Тоон талбар. Утгыг ЗОРИУДААР засварладаггүй — өмнө нь `min` болон "-"-г хасах
 * `parser` нь буруу утгыг чимээгүй өөрчилдөг тул хэрэглэгч бичсэнээсээ өөр утга
 * хадгалагдсаныг мэдэхгүй үлддэг байсан. Шалгуурыг `validation.js`-ийн дүрмүүд
 * хийж, алдааг ил харуулна.
 */
const numberFieldProps = {
  style: { width: '100%' },
};

/** Талбарын тайлбарыг оролтын баруун захад дүрсээр харуулна */
const FieldHint = ({ title, description }) => (
  <Tooltip
    placement="bottomRight"
    overlayInnerStyle={{ backgroundColor: '#fff', color: '#000' }}
    title={
      <div style={{ padding: 8 }}>
        <h4 style={{ color: '#2FA1D9', fontWeight: 600 }}>{title}</h4>
        <p style={{ marginBottom: 0 }}>{description}</p>
      </div>
    }
  >
    <InfoCircleOutlined style={{ color: '#2FA1D9', cursor: 'pointer' }} />
  </Tooltip>
);

const ProductAdd = ({ steps, current, next }) => {
  const [form] = Form.useForm();

  const isBrandAdded = useBrandStore((state) => state.isListChanged);
  const isAutoStockLowProcurement = useStore((state) => state?.user?.isAutoStockLowProcurement);
  const productContext = useContext(LabelContext);
  const sender = productContext.labelInfo.productItem;

  const [categoryList, setCategoryList] = useState([]);
  const [brandList, setBrandList] = useState([]);
  const [categoryLoading, setCategoryLoading] = useState(false);
  const [brandLoading, setBrandLoading] = useState(false);
  const [isErlian, setIsErlian] = useState(false);
  const [isCBM, setIsCBM] = useState(false);
  const [coupon, setCoupon] = useState('');

  if (productContext.productBaseSku) {
    form.setFieldsValue({
      baseSku: productContext.productBaseSku,
    });
  }

  useEffect(() => {
    setCategoryLoading(true);
    axios
      .post('/api/v1.0/product/category')
      .then(({ data }) => {
        if (data.result.activeMerchant === 'EHW') {
          setIsErlian(true);
          productContext.setIsErlian(true);
          setCoupon('COU');
          sender.category = 'COU';
          // Эрээний бараанд категори сонгох талбар харагдахгүй тул нэрийг нь энд авна
          sender.categoryName = data.result.docs?.find((item) => item?.code === 'COU')?.name ?? '';
          sender.brand = ' ';
        } else setIsErlian(false);
        setCategoryList(data.result.docs);
      })
      .catch((e) => {})
      .finally(() => setCategoryLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setBrandLoading(true);
    axios
      .post('/api/v1.0/product/group')
      .then(({ data }) => {
        setBrandList(data.result.docs);
      })
      .catch((e) => {})
      .finally(() => setBrandLoading(false));
  }, [isBrandAdded]);

  useEffect(() => {
    const volume = sender.long * sender.wide * sender.height;
    productContext.onChangeProductVolume(volume);

    //eslint-disable-next-line
  }, [sender.long, sender.wide, sender.height]);

  const onFinish = (data) => {
    // Бөглөсөн текстийн эхэн/төгсгөлийн илүү зай бараан дээр үлдэхээс сэргийлнэ
    const trimmed = trimTextFields(data, TRIMMABLE_TEXT_FIELDS);
    const updates = { ...trimmed };

    if (!sender.hasVariant) {
      /**
       * Өмнө нь `sender`-ыг шууд өөрчилдөг байсан — React-ийн төлөв солигддоггүй
       * тул draft-д хадгалагдахгүй, refresh хийхэд баркод алга болдог байсан.
       */
      const barcodes = data.barcodes ?? [];
      updates.barcodes = barcodes;
      updates.barCode = barcodes.map((barcode) => barcode?.barCode);
    } else {
      generateSKU();
    }

    // Буцаж ирэхэд талбарууд ч цэвэрлэгдсэн утгаа харуулна
    form.setFieldsValue(trimmed);
    productContext.changeProductItems(updates);

    next();
  };

  const onFinishFailed = (values) => {
    const errorNames = values?.errorFields.map((item) => item.name);
    const baseSkuExists = errorNames
      .map((names) => names.includes('baseSku'))
      .find((item) => item === true);
    if (baseSkuExists) {
      message.error('Та SKU кодоо үүсгэнэ үү!', 5);
    }
  };

  const onHandleHasVariant = (checked) => {
    productContext.changeLevelInfo('productItem', 'hasVariant', checked);
    if (!checked) productContext.onChangeVariantList([]);
  };

  const onHandleHasReturn = (event) => {
    productContext.changeLevelInfo('productItem', 'hasReturn', event);
  };

  const onHandleWillExpire = (event) => {
    productContext.changeLevelInfo('productItem', 'willExpire', event);
  };

  const onHandleIsCBM = (event) => {
    setIsCBM(!isCBM);
    const checked = event;
    productContext.changeLevelInfo('productItem', 'isCBM', checked);
  };

  const generateSKU = () => {
    // Аль талбар нь дутуугаа тодорхой мэдэгдэнэ — `sku.js`-ийг үз
    const required = getSkuRequiredFields({ isErlian });
    const missing = findMissingSkuFields(required, sender);

    if (missing.length) {
      // Талбар дээр нь улаанаар тэмдэглэнэ. Товч нь формын доод хэсэгт байдаг
      // тул дээрх талбарууд дэлгэцэнд харагдахгүй байж болзошгүй — мэдэгдлээр
      // нь давхар нэрлэж хэлнэ.
      form.validateFields(required.map(({ name }) => name)).catch(() => {});
      message.error(missingSkuFieldsMessage(missing), 5);
      return;
    }

    const payload = {
      merchantCode: sender.merchantCode,
      categoryCode: sender.category || coupon,
      plainText: sender.detail,
      type: sender.type,
      size: sender.size,
      color: sender.color,
      outputMinLength: 5,
      outputMaxLength: 10,
      textLength: 80,
    };

    axios
      .post('/api/v1.0/product/sku', payload)
      .then(({ data }) => {
        if (data.statusCode === 200) {
          form.setFieldsValue({ baseSku: data.baseSKU });
          productContext.onChangeBaseSku(data.baseSKU);
          message.success('SKU код амжилттай үүслээ!');
        }
      })
      .catch((e) => {});
  };

  return (
    <Form
      form={form}
      name="basic"
      layout="vertical"
      initialValues={{
        ...sender,
      }}
      autoComplete="off"
      onFinish={onFinish}
      onFinishFailed={onFinishFailed}
      /**
       * Бусад талбар өөрчлөгдөх бүрдээ контекстэд бичигддэг (`setProductItem`)
       * бол баркод нь Form.List дотор тул тусад нь хадгална — refresh хийхэд
       * бөглөсөн код нь сэргэнэ.
       */
      onValuesChange={(changedValues, allValues) => {
        if ('barcodes' in changedValues) {
          productContext.changeProductItems({ barcodes: allValues.barcodes });
        }
      }}
    >
      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        <SectionCard icon={<BsBox2 />} title="Барааны мэдээлэл">
          <Row gutter={16} style={{ maxWidth: FORM_MAX_WIDTH }}>
            {!isErlian && (
              <Col xs={24}>
                <Form.Item label="Хувилбартай бараа юу?" name="hasVariant" valuePropName="checked">
                  <Switch onChange={onHandleHasVariant} />
                </Form.Item>
              </Col>
            )}

            <Col xs={24} sm={12} lg={8}>
              <Form.Item label="Барааны нэр" name="title" rules={productTitleRules()}>
                {/* Хязгаарыг тоолуураар шууд харуулж, түүнээс цааш бичихийг хаана */}
                <Input
                  placeholder="Энд бич"
                  maxLength={PRODUCT_TITLE_MAX_LENGTH}
                  showCount
                  onChange={productContext.setProductItem('title')}
                />
              </Form.Item>
            </Col>

            {!isErlian && (
              <Col xs={24} sm={12} lg={8}>
                {/* SKU үүсгэхэд шаардагддаг тул заавал — `sku.js`-ийг үз */}
                <Form.Item
                  label="Бренд"
                  name="brand"
                  rules={[{ required: true, message: 'Бренд сонгоно уу!' }]}
                >
                  <Select
                    placeholder="Сонгох"
                    allowClear
                    showSearch
                    optionFilterProp="label"
                    onChange={productContext.setProductItem('brand')}
                    loading={brandLoading}
                    options={brandList.map((brand) => ({
                      value: brand?.name,
                      label: brand?.name,
                    }))}
                  />
                </Form.Item>
              </Col>
            )}

            {!isErlian && (
              <Col xs={24} sm={12} lg={8}>
                <Form.Item
                  label="Категори"
                  name="category"
                  rules={[{ required: true, message: 'Категори оруулна уу!' }]}
                >
                  <Select
                    placeholder="Сонгох"
                    /*
                     * Сервер код хүлээж авдаг тул `value` нь код. Гэхдээ "Хянах"
                     * алхам болон жагсаалт нь НЭРЭЭР харуулдаг учир сонгосон
                     * нэрийг нь хамт хадгална — эс тэгвээс хянах алхамд гурван
                     * үсэгт код харагдана.
                     */
                    allowClear
                    showSearch
                    // `value` нь код тул НЭРЭЭР нь шүүнэ — эс тэгвээс хэрэглэгч
                    // "Anu" гэж бичихэд юу ч олдохгүй
                    optionFilterProp="label"
                    onChange={(value, option) =>
                      productContext.changeProductItems({
                        category: value,
                        categoryName: option?.label ?? '',
                      })
                    }
                    loading={categoryLoading}
                    options={categoryList.map((category) => ({
                      value: category?.code,
                      label: category?.name,
                    }))}
                  />
                </Form.Item>
              </Col>
            )}

            <Col xs={24}>
              <Form.Item
                label="Барааны тайлбар"
                name="detail"
                rules={productDescriptionRules(!isErlian)}
              >
                <TextArea
                  rows={3}
                  placeholder="Энд бич"
                  maxLength={PRODUCT_DESCRIPTION_MAX_LENGTH}
                  showCount
                  onChange={productContext.setProductItem('detail')}
                />
              </Form.Item>
            </Col>

            {!isErlian && (
              <>
                <Col xs={24} sm={12} lg={8}>
                  <Form.Item
                    label="Буцаагддаг бараа эсэх?"
                    name="hasReturn"
                    valuePropName="checked"
                    style={{ marginBottom: 0 }}
                  >
                    <Switch onChange={onHandleHasReturn} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} lg={8}>
                  <Form.Item
                    label="Дуусах хугацаатай эсэх?"
                    name="willExpire"
                    valuePropName="checked"
                    style={{ marginBottom: 0 }}
                  >
                    <Switch onChange={onHandleWillExpire} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} lg={8}>
                  <Form.Item
                    label={'Эвдрэх аюултай эсэх'}
                    name="isFragile"
                    valuePropName="checked"
                    style={{ marginBottom: 0 }}
                  >
                    <Switch onChange={productContext.setProductItem('isFragile')} />
                  </Form.Item>
                </Col>
              </>
            )}
          </Row>
        </SectionCard>

        <SectionCard icon={<BsBoxSeam />} title="Нөөцийн мэдээлэл">
          <Row gutter={16} style={{ maxWidth: FORM_MAX_WIDTH }}>
            <Col xs={24} sm={12} lg={8}>
              <Form.Item
                label="Зайлшгүй байх нөөц"
                name="minStock"
                rules={[
                  { required: true, message: 'Зайлшгүй байх нөөц оруулна уу!' },
                  wholeNumberRule('Зайлшгүй байх нөөц'),
                ]}
                style={{ marginBottom: 0 }}
              >
                <InputNumber
                  {...numberFieldProps}
                  name="minStock"
                  onChange={productContext.setProductItem('minStock')}
                />
              </Form.Item>
            </Col>

            {isAutoStockLowProcurement && (
              <Col xs={24} sm={12} lg={8}>
                <Form.Item
                  label="Дахин захиалга хийх тоо хэмжээ"
                  name="reorderQuantity"
                  rules={[
                    { required: true, message: 'Дахин захиалга хийх тоо хэмжээ оруулна уу!' },
                    wholeNumberRule('Дахин захиалга хийх тоо хэмжээ'),
                  ]}
                  style={{ marginBottom: 0 }}
                >
                  <InputNumber
                    {...numberFieldProps}
                    name="reorderQuantity"
                    onChange={productContext.setProductItem('reorderQuantity')}
                  />
                </Form.Item>
              </Col>
            )}
          </Row>
        </SectionCard>

        <SectionCard icon={<BsRulers />} title="Барааны жин, эзлэхүүн">
          <Row gutter={16} style={{ maxWidth: FORM_MAX_WIDTH }}>
            {!isErlian && (
              <Col xs={24}>
                <Form.Item label="Эзэлхүүн оруулах эсэх" name="isCBM" valuePropName="checked">
                  <Switch onChange={onHandleIsCBM} />
                </Form.Item>
              </Col>
            )}

            {!isCBM ? (
              <>
                <Col xs={12} sm={12} lg={6}>
                  <Form.Item
                    label="Урт"
                    name="long"
                    rules={[
                      {
                        pattern: new RegExp(commonRegExp.greaterThanZero),
                        message: 'Урт 0-с их байх ёстой!',
                      },
                    ]}
                  >
                    <InputNumber
                      {...numberFieldProps}
                      name="long"
                      placeholder="Энд бич"
                      onChange={productContext.setProductItem('long')}
                    />
                  </Form.Item>
                </Col>

                <Col xs={12} sm={12} lg={6}>
                  <Form.Item
                    label="Өргөн"
                    name="wide"
                    rules={[
                      {
                        pattern: new RegExp(commonRegExp.greaterThanZero),
                        message: 'Өргөн 0-с их байх ёстой!',
                      },
                    ]}
                  >
                    <InputNumber
                      {...numberFieldProps}
                      name="wide"
                      placeholder="Энд бич"
                      onChange={productContext.setProductItem('wide')}
                    />
                  </Form.Item>
                </Col>

                <Col xs={12} sm={12} lg={6}>
                  <Form.Item
                    label="Өндөр"
                    name="height"
                    rules={[
                      {
                        pattern: new RegExp(commonRegExp.greaterThanZero),
                        message: 'Өндөр 0-с их байх ёстой!',
                      },
                    ]}
                  >
                    <InputNumber
                      {...numberFieldProps}
                      name="height"
                      placeholder="Энд бич"
                      onChange={productContext.setProductItem('height')}
                    />
                  </Form.Item>
                </Col>
              </>
            ) : (
              <Col xs={12} sm={12} lg={6}>
                <Form.Item
                  label="Эзэлхүүн"
                  name="cbm"
                  rules={[
                    {
                      pattern: new RegExp(commonRegExp.greaterThanZero),
                      message: 'Эзэлхүүн 0-с их байх ёстой!',
                    },
                  ]}
                >
                  <InputNumber
                    {...numberFieldProps}
                    name="cbm"
                    placeholder="Энд бич"
                    onChange={productContext.setProductItem('cbm')}
                  />
                </Form.Item>
              </Col>
            )}

            <Col xs={12} sm={12} lg={6}>
              {/* `name` байхгүй тул утга нь контекстээс шууд удирдагдана */}
              <Form.Item label="Хэмжих нэгж">
                <Select
                  value={sender.volumeUnit}
                  onChange={productContext.setProductItem('volumeUnit')}
                  options={getProductSizeOptions()}
                />
              </Form.Item>
            </Col>

            <Col xs={12} sm={12} lg={6}>
              <Form.Item label="Жин хэмжих нэгж" style={{ marginBottom: 0 }}>
                <Select
                  value={sender.weightUnit}
                  onChange={productContext.setProductItem('weightUnit')}
                  options={getProductWeightOptions()}
                />
              </Form.Item>
            </Col>

            <Col xs={12} sm={12} lg={6}>
              <Form.Item
                label="Жин"
                name="weight"
                rules={[
                  {
                    required: true,
                    message: 'Жин оруулна уу!',
                    type: 'number',
                  },
                  {
                    pattern: new RegExp(commonRegExp.greaterThanZero),
                    message: 'Жин 0-ээс их байх ёстой!',
                  },
                ]}
                style={{ marginBottom: 0 }}
              >
                <InputNumber
                  {...numberFieldProps}
                  placeholder="Энд бич"
                  onChange={productContext.setProductItem('weight')}
                />
              </Form.Item>
            </Col>
          </Row>
        </SectionCard>

        {!sender?.hasVariant && (
          <SectionCard icon={<BsImages />} title="Барааны зураг">
            <Row gutter={16} style={{ maxWidth: FORM_MAX_WIDTH }}>
              <Col>
                <Form.Item
                  label="Үндсэн зураг"
                  name="image"
                  rules={[{ required: true, message: 'Үндсэн зураг оруулна уу!' }]}
                  style={{ marginBottom: 0 }}
                >
                  <SingleImageUpload />
                </Form.Item>
              </Col>
              <Col flex="auto">
                {/* Нэмэлт зургууд нь контекстэд өөрөө хадгалагддаг тул `name` шаардлагагүй */}
                <Form.Item label="Нэмэлт зураг" style={{ marginBottom: 0 }}>
                  <ImageUpload />
                </Form.Item>
              </Col>
            </Row>
          </SectionCard>
        )}

        <SectionCard icon={<AiOutlineInfoCircle />} title="Шинж чанар, үнэ">
          <Row gutter={16} style={{ maxWidth: FORM_MAX_WIDTH }}>
            <Col xs={24} sm={12} lg={8}>
              <Form.Item label="Хэмжээ" name="size">
                <Input
                  placeholder="Энд бич"
                  onChange={productContext.setProductItem('size')}
                  suffix={
                    <FieldHint
                      title="Хэмжээ гэж юу вэ?"
                      description="Хэмжээ нь тухайн барааны шатлал буюу жижиг, дунд, том гэх мэт хэмжээсүүдийг хэлнэ."
                    />
                  }
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12} lg={8}>
              <Form.Item label="Өнгө" name="color">
                <Input
                  name="color"
                  placeholder="Энд бич"
                  onChange={productContext.setProductItem('color')}
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12} lg={8}>
              <Form.Item label="Төрөл" name="type">
                <Input
                  placeholder="Энд бич"
                  onChange={productContext.setProductItem('type')}
                  suffix={
                    <FieldHint
                      title="Төрөл гэж юу вэ?"
                      description="Төрөл нь тухайн барааны ямар төрөл ангилал болохыг хэлнэ."
                    />
                  }
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12} lg={8}>
              <Form.Item
                label={isErlian ? 'Тээврийн үнэ' : 'Зарах үнэ'}
                name="price"
                rules={[
                  { required: true, message: 'Зарах үнэ оруулна уу!' },
                  nonNegativeRule('Зарах үнэ'),
                ]}
                style={{ marginBottom: 0 }}
              >
                <InputNumber
                  {...numberFieldProps}
                  placeholder="Энд бич"
                  formatter={formatThousands}
                  parser={parseThousands}
                  onChange={productContext.setProductItem('price')}
                />
              </Form.Item>
            </Col>
          </Row>
        </SectionCard>

        {!sender?.hasVariant && (
          <SectionCard icon={<BsUpcScan />} title="Код">
            <Row gutter={16} style={{ maxWidth: FORM_MAX_WIDTH }}>
              <Col xs={24} sm={12} lg={8}>
                <Form.Item label="SKU код" required>
                  <Space.Compact style={{ width: '100%' }}>
                    <Form.Item
                      name="baseSku"
                      noStyle
                      rules={[{ required: true, message: 'SKU код үүсгэнэ үү!' }]}
                    >
                      <Input disabled placeholder="Код үүсгэх" />
                    </Form.Item>
                    <Button
                      type="primary"
                      icon={<ReloadOutlined />}
                      onClick={generateSKU}
                      title="SKU код үүсгэх"
                    />
                  </Space.Compact>
                </Form.Item>
              </Col>

              <Form.List name="barcodes">
                {(fields, { add, remove }) => (
                  <>
                    {fields.map(({ key, name, ...restField }) => (
                      <Col key={key} xs={24} sm={12} lg={8}>
                        <Form.Item
                          {...restField}
                          label={isErlian ? `Тракны дугаар ${name + 1}` : `Код ${name + 1}`}
                          name={[name, 'barCode']}
                          rules={[requiredText('Код оруулна уу!')]}
                        >
                          <Input
                            placeholder="Код оруулна уу"
                            suffix={
                              <MinusCircleOutlined
                                onClick={() => remove(name)}
                                style={{ color: '#8993a5', cursor: 'pointer' }}
                              />
                            }
                          />
                        </Form.Item>
                      </Col>
                    ))}

                    <Col xs={24} sm={12} lg={8}>
                      {/* Талбартай нэг эгнээнд байрлуулахын тулд хоосон label үлдээв */}
                      <Form.Item label={fields.length ? ' ' : 'Код'}>
                        <Button
                          onClick={() => add()}
                          block
                          icon={<PlusOutlined />}
                          style={{ color: '#4BBEDA', borderColor: '#4BBEDA' }}
                        >
                          {isErlian ? 'Трак нэмэх' : 'Код нэмэх'}
                        </Button>
                      </Form.Item>
                    </Col>
                  </>
                )}
              </Form.List>
            </Row>
          </SectionCard>
        )}

        {current < steps.length - 1 && (
          <FormFooter>
            <Link to="/merchant/product/list">
              <Button htmlType="button">Буцах</Button>
            </Link>
            <Button type="primary" htmlType="submit">
              Үргэлжлүүлэх
            </Button>
          </FormFooter>
        )}
      </Space>
    </Form>
  );
};

export default ProductAdd;
